import { LOCAL_STORAGE_KEY } from '@core/configs';
import { normalizeSettingHeaders, NormalizeSettingHeadersOptions } from '@core/helper';
import { CommonHelper } from '@core/services/common';
import { shortTimeGridConfig, shortTimeSettingDialogExcludedFields } from '@modules/check-sheet/short-time/check-sheet-short-time.config';

type PrepareHeaderFunction = (header: any) => any;

// Public helpers used by the TV setting menu.
export function getShortTimeSetting(settingData: any, prepareHeader: PrepareHeaderFunction): any {
    const screenSetting = getShortTimeScreenSetting(settingData);
    const mergedHeaders = mergeShortTimeHeaders(
        screenSetting?.headers,
        prepareHeader
    );
    const sortConfig = getShortTimeDisplaySortConfig(settingData);
    const shortTimeSetting = {
        ...settingData,
        headers: mergedHeaders.headers,
        sortConfig,
    };

    if (mergedHeaders.changed || isMissingShortTimeSortConfig(settingData)) {
        CommonHelper.setScreenConfigByOfficeCd(prepareShortTimeSettingForStorage(shortTimeSetting));
    }

    return shortTimeSetting;
}

export function getShortTimeHeaders(headers: any[], prepareHeader: PrepareHeaderFunction): any[] {
    return mergeShortTimeHeaders(headers, prepareHeader).headers;
}

export function getShortTimeDefaultConfig(): any {
    return getShortTimeDefaultSortConfig();
}

export function prepareShortTimeSettingForStorage(config: any): any {
    const storedConfig = getStoredScreenConfig() ?? {};
    const { headers, sortConfig, ...rootConfig } = config;
    const screenKey = LOCAL_STORAGE_KEY.settingDialogShortTime;
    const screenSetting = storedConfig[screenKey] ?? {};

    return {
        ...storedConfig,
        ...rootConfig,
        [screenKey]: createShortTimeStorageConfig(screenSetting, headers, sortConfig),
    };
}

// Runtime setting merge helpers.
function mergeShortTimeHeaders(headers: any[], prepareHeader: PrepareHeaderFunction): any {
    const options:NormalizeSettingHeadersOptions = {
            excludedFields: ['index', ...shortTimeSettingDialogExcludedFields],
            prepareHeader,
    }
    
    return normalizeSettingHeaders(
        addReasonLeaveHeader(shortTimeGridConfig.header),
        headers,
        options
    );
}

function addReasonLeaveHeader(headers: any[]): any[] {
    if (headers?.some((header: any) => header?.field === 'leave_reason')) return headers;
    return [
        ...headers,
        {
            field: 'leave_reason',
            headerName: '中止理由',
            visible: true,
        },
    ];
}

function getShortTimeDisplaySortConfig(settingData: any): any {
    return {
        ...getCommonSortConfig(settingData?.sortConfig),
        ...getShortTimeSortConfig(getShortTimeScreenSetting(settingData)?.sortConfig),
    };
}

function getShortTimeDefaultSortConfig(): any {
    return {
        ...getCommonSortConfig(null),
        ...shortTimeGridConfig.defaultSortConfig,
    };
}

// Saved short-time setting readers.
function getShortTimeScreenSetting(settingData: any): any {
    return settingData?.[LOCAL_STORAGE_KEY.settingDialogShortTime];
}

// Storage preparation helpers.
function createShortTimeStorageConfig(screenSetting: any, headers: any[], sortConfig: any): any {
    return {
        ...(screenSetting ?? {}),
        headers,
        sortConfig: getShortTimeSortConfig(sortConfig),
    };
}

function getShortTimeSortConfig(sortConfig: any): any {
    return {
        display_order: sortConfig?.display_order ?? shortTimeGridConfig.defaultSortConfig.display_order,
        time_grouping_flg: sortConfig?.time_grouping_flg ?? shortTimeGridConfig.defaultSortConfig.time_grouping_flg,
        sorting_time: sortConfig?.sorting_time ?? shortTimeGridConfig.defaultSortConfig.sorting_time,
    };
}

function getCommonSortConfig(sortConfig: any): any {
    return {
        hidden_overtime_flg: sortConfig?.hidden_overtime_flg ?? false,
        hidden_leave_user: sortConfig?.hidden_leave_user ?? false,
        unitSelected: sortConfig?.unitSelected ?? [],
        groupSelected: sortConfig?.groupSelected ?? [],
    };
}

function getStoredScreenConfig(): any {
    try {
        return CommonHelper.getScreenConfigByOfficeCd();
    } catch (error) {
        return null;
    }
}

function isMissingShortTimeSortConfig(settingData: any): boolean {
    return !getShortTimeScreenSetting(settingData)?.sortConfig;
}
